import type React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  Chip,
  Box,
} from "@mui/material";
import { CheckCircle, Star } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

interface Package {
  id: number;
  name: string;
  price: number;
  contents: string[];
  package_type: string;
  recommended?: boolean;
}

interface PackageSelectorProps {
  packages: Package[];
  selectedPackage: string;
  onPackageSelect: (packageName: string) => void;
}

const PackageSelector: React.FC<PackageSelectorProps> = ({
  packages,
  selectedPackage,
  onPackageSelect,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  return (
    <Card sx={{ mb: 4, boxShadow: 0 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Select Service Package(recommended)
        </Typography>
        <Grid container spacing={3}>
          {packages.map((pkg) => (
            <Grid size={{ xs: 12, md: 4 }} key={pkg.id}>
              <Card
                sx={{
                  position: "relative",
                  boxShadow: 0,
                  border:
                    selectedPackage === pkg.name ? "2px solid" : "1px solid",
                  borderColor:
                    selectedPackage === pkg.name
                      ? reduxTheme.primary_colors[400]
                      : "divider",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: 4,
                    borderColor: reduxTheme.primary_colors[400],
                  },
                }}
                onClick={() => onPackageSelect(pkg.name)}
              >
                {pkg.recommended && (
                  <Chip
                    label="Recommended"
                    color="primary"
                    size="small"
                    icon={<Star />}
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      zIndex: 1,
                    }}
                  />
                )}

                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {pkg.name}
                  </Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    ${pkg.price}
                  </Typography>

                  <List dense>
                    {pkg.contents.slice(0, 5).map((feature, index) => (
                      <ListItem
                        key={index}
                        disablePadding
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckCircle
                          sx={{
                            fontSize: 16,
                            mt: "4px",
                            color: "success.main",
                          }}
                        />
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            "& p": { margin: 0 },
                            "& div": { margin: 0 },
                          }}
                          dangerouslySetInnerHTML={{ __html: feature }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PackageSelector;
